import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { getReleases, deleteRelease, getDSPServices, getSocialPlatforms, createDSPService, createSocialPlatform, deleteDSPService, deleteSocialPlatform, getMe, getPromotionClients, addPromotionClient, deletePromotionClient, togglePromotionClientFeatured, getNews, createNews, deleteNews, getProducts, createProduct, updateProduct, deleteProduct, getOrders, updateOrderStatus, getPromoArtistImages, setPromoArtistImage, deletePromoArtistImage, uploadPromoArtistImage, getTribeArtists, getHiddenArtists, hideManagementArtist, showManagementArtist } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FaPlus, FaEdit, FaTrash, FaMusic, FaSignOutAlt, FaSpotify, FaInstagram, FaHome, FaCode, FaNewspaper, FaBullhorn, FaExternalLinkAlt, FaShoppingBag, FaBox, FaImage, FaStar, FaUsers, FaEye, FaEyeSlash } from "react-icons/fa";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [releases, setReleases] = useState([]);
  const [dspServices, setDspServices] = useState([]);
  const [socialPlatforms, setSocialPlatforms] = useState([]);
  const [promotionClients, setPromotionClients] = useState([]);
  const [newsArticles, setNewsArticles] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [promoImages, setPromoImages] = useState([]);
  const [managementArtists, setManagementArtists] = useState([]);
  const [hiddenArtists, setHiddenArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDSP, setNewDSP] = useState({ name: "", icon: "" });
  const [newSocial, setNewSocial] = useState({ name: "", icon: "" });
  const [newClient, setNewClient] = useState("");
  const [newArticle, setNewArticle] = useState({ title: "", url: "", source: "", artist: "" });
  const [newProduct, setNewProduct] = useState({ name: "", description: "", price: "", images: "", sizes: "S,M,L,XL", stock: "5" });
  const [newPromoImage, setNewPromoImage] = useState({ name: "", file: null });
  const [dspDialogOpen, setDspDialogOpen] = useState(false);
  const [socialDialogOpen, setSocialDialogOpen] = useState(false);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [newsDialogOpen, setNewsDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [promoImageDialogOpen, setPromoImageDialogOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await getMe();
        fetchData();
      } catch {
        navigate("/admin");
      }
    };
    checkAuth();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [releasesRes, dspRes, socialRes, clientsRes, newsRes, productsRes, ordersRes, promoImagesRes, tribeArtistsRes, hiddenRes] = await Promise.all([
        getReleases(),
        getDSPServices(),
        getSocialPlatforms(),
        getPromotionClients(),
        getNews(),
        getProducts(),
        getOrders().catch(() => ({ data: [] })),
        getPromoArtistImages().catch(() => ({ data: [] })),
        getTribeArtists().catch(() => ({ data: [] })),
        getHiddenArtists().catch(() => ({ data: [] })),
      ]);
      setReleases(releasesRes.data);
      setDspServices(dspRes.data);
      setSocialPlatforms(socialRes.data);
      setPromotionClients(clientsRes.data || []);
      setNewsArticles(newsRes.data || []);
      setProducts(productsRes.data || []);
      setOrders(ordersRes.data || []);
      setPromoImages(promoImagesRes.data || []);
      setManagementArtists(tribeArtistsRes.data || []);
      setHiddenArtists(hiddenRes.data || []);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const handleAddProduct = async () => {
    if (!newProduct.name || !newProduct.price) {
      toast.error("Please enter product name and price");
      return;
    }
    try {
      const sizes = newProduct.sizes.split(",").map(s => ({ size: s.trim(), stock: parseInt(newProduct.stock) || 5 }));
      const images = newProduct.images ? [newProduct.images] : [];
      const productData = {
        name: newProduct.name,
        description: newProduct.description,
        price: parseFloat(newProduct.price),
        images,
        sizes,
        category: "clothing",
        is_active: true
      };
      const res = await createProduct(productData);
      setProducts([...products, res.data]);
      setNewProduct({ name: "", description: "", price: "", images: "", sizes: "S,M,L,XL", stock: "5" });
      setProductDialogOpen(false);
      toast.success("Product added");
    } catch (error) {
      toast.error("Failed to add product");
    }
  };

  const handleDeleteProduct = async (id) => {
    try {
      await deleteProduct(id);
      setProducts(products.filter(p => p.id !== id));
      toast.success("Product deleted");
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await updateOrderStatus(orderId, status);
      setOrders(orders.map(o => o.id === orderId ? { ...o, status } : o));
      toast.success("Order status updated");
    } catch (error) {
      toast.error("Failed to update order status");
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(price);
  };

  const handleAddPromoImage = async () => {
    if (!newPromoImage.name || !newPromoImage.file) {
      toast.error("Please enter artist name and select an image file");
      return;
    }
    try {
      setUploadingImage(true);
      const res = await uploadPromoArtistImage(newPromoImage.name, newPromoImage.file);
      const uploadedImage = { name: res.data.name, image_url: res.data.image_url };
      setPromoImages([...promoImages.filter(p => p.name.toLowerCase() !== newPromoImage.name.toLowerCase()), uploadedImage]);
      setNewPromoImage({ name: "", file: null });
      setPromoImageDialogOpen(false);
      toast.success("Artist image uploaded");
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeletePromoImage = async (name) => {
    try {
      await deletePromoArtistImage(name);
      setPromoImages(promoImages.filter(p => p.name.toLowerCase() !== name.toLowerCase()));
      toast.success("Image override removed");
    } catch (error) {
      toast.error("Failed to remove image");
    }
  };

  const handleToggleArtistVisibility = async (artistId, artistName, isHidden) => {
    try {
      if (isHidden) {
        await showManagementArtist(artistId);
        setHiddenArtists(hiddenArtists.filter(id => id !== artistId));
        toast.success(`${artistName} is now visible`);
      } else {
        await hideManagementArtist(artistId, artistName);
        setHiddenArtists([...hiddenArtists, artistId]);
        toast.success(`${artistName} is now hidden`);
      }
    } catch (error) {
      toast.error("Failed to update visibility");
    }
  };

  const handleDeleteRelease = async (id) => {
    try {
      await deleteRelease(id);
      setReleases(releases.filter((r) => r.id !== id));
      toast.success("Release deleted");
    } catch (error) {
      toast.error("Failed to delete release");
    }
  };

  const handleAddDSP = async () => {
    if (!newDSP.name || !newDSP.icon) {
      toast.error("Please fill all fields");
      return;
    }
    try {
      const res = await createDSPService(newDSP);
      setDspServices([...dspServices, res.data]);
      setNewDSP({ name: "", icon: "" });
      setDspDialogOpen(false);
      toast.success("DSP Service added");
    } catch (error) {
      toast.error("Failed to add DSP service");
    }
  };

  const handleAddSocial = async () => {
    if (!newSocial.name || !newSocial.icon) {
      toast.error("Please fill all fields");
      return;
    }
    try {
      const res = await createSocialPlatform(newSocial);
      setSocialPlatforms([...socialPlatforms, res.data]);
      setNewSocial({ name: "", icon: "" });
      setSocialDialogOpen(false);
      toast.success("Social platform added");
    } catch (error) {
      toast.error("Failed to add social platform");
    }
  };

  const handleDeleteDSP = async (id) => {
    try {
      await deleteDSPService(id);
      setDspServices(dspServices.filter((d) => d.id !== id));
      toast.success("DSP Service deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete DSP service");
    }
  };

  const handleDeleteSocial = async (id) => {
    try {
      await deleteSocialPlatform(id);
      setSocialPlatforms(socialPlatforms.filter((s) => s.id !== id));
      toast.success("Social platform deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete social platform");
    }
  };

  const handleAddClient = async () => {
    if (!newClient.trim()) {
      toast.error("Please enter a client name");
      return;
    }
    try {
      const res = await addPromotionClient(newClient.trim());
      setPromotionClients([...promotionClients, res.data]);
      setNewClient("");
      setClientDialogOpen(false);
      toast.success("Promotion client added");
    } catch (error) {
      toast.error("Failed to add promotion client");
    }
  };

  const handleDeleteClient = async (id) => {
    try {
      await deletePromotionClient(id);
      setPromotionClients(promotionClients.filter((c) => c.id !== id));
      toast.success("Promotion client deleted");
    } catch (error) {
      toast.error("Failed to delete promotion client");
    }
  };

  const handleToggleFeatured = async (id, currentFeatured) => {
    try {
      await togglePromotionClientFeatured(id, !currentFeatured);
      setPromotionClients(promotionClients.map(c => 
        c.id === id ? { ...c, featured: !currentFeatured } : c
      ));
      toast.success(currentFeatured ? "Removed from carousel" : "Added to carousel");
    } catch (error) {
      toast.error("Failed to update featured status");
    }
  };

  const handleAddNews = async () => {
    if (!newArticle.title || !newArticle.url) {
      toast.error("Please enter title and URL");
      return;
    }
    try {
      const res = await createNews(newArticle);
      setNewsArticles([res.data, ...newsArticles]);
      setNewArticle({ title: "", url: "", source: "", artist: "" });
      setNewsDialogOpen(false);
      toast.success("News article added");
    } catch (error) {
      toast.error("Failed to add news article");
    }
  };

  const handleDeleteNews = async (id) => {
    try {
      await deleteNews(id);
      setNewsArticles(newsArticles.filter((n) => n.id !== id));
      toast.success("News article deleted");
    } catch (error) {
      toast.error("Failed to delete news article");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse">
          <FaMusic className="w-12 h-12 text-accent-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_music-connect-23/artifacts/mbsj96z9_UNDERPLAY%20MUSIC%20LOGO%20NEW%202025%20no%20border%20no%20TEXT.png" 
              alt="Underplay" 
              className="h-8 w-auto"
            />
            <h1 className="text-xl font-syne font-bold text-white">Underplay Admin</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/embed-code">
              <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white" data-testid="embed-code-btn">
                <FaCode className="mr-2" /> Embed Code
              </Button>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white" data-testid="view-site-btn">
                <FaHome className="mr-2" /> View Site
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-zinc-400 hover:text-white"
              data-testid="logout-btn"
            >
              <FaSignOutAlt className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs defaultValue="releases" className="space-y-6">
          <TabsList className="bg-background-surface border border-zinc-800 flex-wrap">
            <TabsTrigger value="releases" className="data-[state=active]:bg-accent-primary data-[state=active]:text-white" data-testid="releases-tab">
              Releases
            </TabsTrigger>
            <TabsTrigger value="products" className="data-[state=active]:bg-accent-primary data-[state=active]:text-white" data-testid="products-tab">
              Products
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-accent-primary data-[state=active]:text-white" data-testid="orders-tab">
              Orders
            </TabsTrigger>
            <TabsTrigger value="management" className="data-[state=active]:bg-accent-primary data-[state=active]:text-white" data-testid="management-tab">
              Management
            </TabsTrigger>
            <TabsTrigger value="promotion" className="data-[state=active]:bg-accent-primary data-[state=active]:text-white" data-testid="promotion-tab">
              Promotion
            </TabsTrigger>
            <TabsTrigger value="news" className="data-[state=active]:bg-accent-primary data-[state=active]:text-white" data-testid="news-tab">
              News
            </TabsTrigger>
            <TabsTrigger value="dsp" className="data-[state=active]:bg-accent-primary data-[state=active]:text-white" data-testid="dsp-tab">
              DSP
            </TabsTrigger>
            <TabsTrigger value="social" className="data-[state=active]:bg-accent-primary data-[state=active]:text-white" data-testid="social-tab">
              Social
            </TabsTrigger>
          </TabsList>

          {/* Releases Tab */}
          <TabsContent value="releases">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-syne font-bold text-white">Releases</h2>
                <Link to="/admin/release/new">
                  <Button className="bg-accent-primary hover:bg-accent-secondary rounded-full" data-testid="add-release-btn">
                    <FaPlus className="mr-2" /> Add Release
                  </Button>
                </Link>
              </div>

              {releases.length === 0 ? (
                <div className="text-center py-12 bg-background-surface rounded-xl border border-zinc-800" data-testid="no-releases-admin">
                  <FaMusic className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
                  <p className="text-zinc-500">No releases yet. Add your first release!</p>
                </div>
              ) : (
                <div className="bg-background-surface rounded-xl border border-zinc-800 overflow-hidden" data-testid="releases-table">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800 hover:bg-zinc-900/50">
                        <TableHead className="text-zinc-400">Artwork</TableHead>
                        <TableHead className="text-zinc-400">Title</TableHead>
                        <TableHead className="text-zinc-400">Artist</TableHead>
                        <TableHead className="text-zinc-400">Release Date</TableHead>
                        <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {releases.map((release) => (
                        <TableRow key={release.id} className="border-zinc-800 hover:bg-zinc-900/50" data-testid={`release-row-${release.id}`}>
                          <TableCell>
                            <img
                              src={release.artwork_url}
                              alt={release.title}
                              className="w-12 h-12 rounded object-cover"
                            />
                          </TableCell>
                          <TableCell className="font-medium text-white">{release.title}</TableCell>
                          <TableCell className="text-zinc-400">{release.artist}</TableCell>
                          <TableCell className="text-zinc-400">{formatDate(release.release_date)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Link to={`/admin/release/${release.id}`}>
                                <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white" data-testid={`edit-release-${release.id}`}>
                                  <FaEdit />
                                </Button>
                              </Link>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-red-500" data-testid={`delete-release-${release.id}`}>
                                    <FaTrash />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="bg-background-surface border-zinc-800">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle className="text-white">Delete Release?</AlertDialogTitle>
                                    <AlertDialogDescription className="text-zinc-400">
                                      This will permanently delete "{release.title}". This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel className="border-zinc-700 text-zinc-300">Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteRelease(release.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                      data-testid={`confirm-delete-${release.id}`}
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </motion.div>
          </TabsContent>

          {/* Management Tab */}
          <TabsContent value="management">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-syne font-bold text-white">Management Artists</h2>
              </div>
              <p className="text-zinc-500 text-sm mb-2">
                Control which artists appear on the Management page. Hidden artists are kept in the system but not displayed on the website.
              </p>
              <p className="text-zinc-600 text-xs mb-4">
                Visible: {managementArtists.length - hiddenArtists.length} | Hidden: {hiddenArtists.length} | Total: {managementArtists.length}
              </p>

              {managementArtists.length === 0 ? (
                <div className="text-center py-12 bg-background-surface rounded-xl border border-zinc-800">
                  <FaUsers className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
                  <p className="text-zinc-500">No management artists found. Make sure the Tribe API is connected.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {managementArtists
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((artist) => {
                      const isHidden = hiddenArtists.includes(artist.id);
                      return (
                        <div
                          key={artist.id}
                          className={`bg-background-surface border rounded-lg p-3 flex items-center justify-between ${
                            isHidden ? 'border-red-500/30 opacity-60' : 'border-zinc-800'
                          }`}
                        >
                          <span className={`text-sm truncate ${isHidden ? 'text-zinc-500 line-through' : 'text-zinc-300'}`}>
                            {artist.name}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleArtistVisibility(artist.id, artist.name, isHidden)}
                            className={isHidden ? 'text-red-400 hover:text-green-400' : 'text-green-400 hover:text-red-400'}
                            title={isHidden ? 'Show artist' : 'Hide artist'}
                          >
                            {isHidden ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                          </Button>
                        </div>
                      );
                    })}
                </div>
              )}
            </motion.div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-syne font-bold text-white">Products</h2>
                <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-accent-primary hover:bg-accent-secondary rounded-full" data-testid="add-product-btn">
                      <FaPlus className="mr-2" /> Add Product
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-background-surface border-zinc-800">
                    <DialogHeader>
                      <DialogTitle className="text-white font-syne">Add Product</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label className="text-zinc-400">Product Name *</Label>
                        <Input
                          value={newProduct.name}
                          onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                          placeholder="e.g., MA1 Bomber Jacket"
                          className="bg-zinc-900 border-zinc-700 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Description</Label>
                        <Input
                          value={newProduct.description}
                          onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                          placeholder="Product description"
                          className="bg-zinc-900 border-zinc-700 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Price (GBP) *</Label>
                        <Input
                          type="number"
                          value={newProduct.price}
                          onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                          placeholder="150"
                          className="bg-zinc-900 border-zinc-700 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Image URL</Label>
                        <Input
                          value={newProduct.images}
                          onChange={(e) => setNewProduct({ ...newProduct, images: e.target.value })}
                          placeholder="https://..."
                          className="bg-zinc-900 border-zinc-700 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Sizes (comma separated)</Label>
                        <Input
                          value={newProduct.sizes}
                          onChange={(e) => setNewProduct({ ...newProduct, sizes: e.target.value })}
                          placeholder="S,M,L,XL"
                          className="bg-zinc-900 border-zinc-700 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Stock per size</Label>
                        <Input
                          type="number"
                          value={newProduct.stock}
                          onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                          placeholder="5"
                          className="bg-zinc-900 border-zinc-700 text-white mt-1"
                        />
                      </div>
                      <Button onClick={handleAddProduct} className="w-full bg-accent-primary hover:bg-accent-secondary rounded-full">
                        Add Product
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <p className="text-zinc-500 text-sm">Manage your shop products. Total: {products.length} products</p>

              {products.length === 0 ? (
                <div className="text-center py-12 bg-background-surface rounded-xl border border-zinc-800">
                  <FaShoppingBag className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
                  <p className="text-zinc-500">No products yet. Add your first product!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map((product) => (
                    <div key={product.id} className="bg-background-surface border border-zinc-800 rounded-xl p-4">
                      <div className="aspect-square rounded-lg bg-zinc-900 mb-3 overflow-hidden">
                        {product.images?.[0] ? (
                          <img src={product.images[0]} alt={product.name} className="w-full h-full object-contain p-2" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <FaShoppingBag className="w-8 h-8 text-zinc-700" />
                          </div>
                        )}
                      </div>
                      <h3 className="font-medium text-white">{product.name}</h3>
                      <p className="text-zinc-400">{formatPrice(product.price)}</p>
                      <p className="text-xs text-zinc-500 mt-1">
                        Sizes: {product.sizes?.map(s => s.size).join(", ")}
                      </p>
                      <div className="flex justify-end mt-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteProduct(product.id)}
                          className="text-zinc-500 hover:text-red-500"
                        >
                          <FaTrash className="mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <h2 className="text-2xl font-syne font-bold text-white">Orders</h2>
              <p className="text-zinc-500 text-sm">Manage customer orders. Total: {orders.length} orders</p>

              {orders.length === 0 ? (
                <div className="text-center py-12 bg-background-surface rounded-xl border border-zinc-800">
                  <FaBox className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
                  <p className="text-zinc-500">No orders yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div key={order.id} className="bg-background-surface border border-zinc-800 rounded-xl p-5">
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                              order.status === "paid" ? "bg-green-900 text-green-300" :
                              order.status === "shipped" ? "bg-blue-900 text-blue-300" :
                              order.status === "delivered" ? "bg-purple-900 text-purple-300" :
                              order.status === "cancelled" ? "bg-red-900 text-red-300" :
                              "bg-yellow-900 text-yellow-300"
                            }`}>
                              {order.status?.toUpperCase()}
                            </span>
                            <span className="text-xl font-bold text-white">{formatPrice(order.total)}</span>
                          </div>
                          <p className="text-xs text-zinc-500 font-mono">Order #{order.id?.slice(0, 8)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-zinc-500">{new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                          <p className="text-xs text-zinc-600">{new Date(order.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Items */}
                        <div className="bg-zinc-900/50 rounded-lg p-3">
                          <h4 className="text-xs font-bold text-zinc-400 mb-2 uppercase">Items</h4>
                          <div className="space-y-1">
                            {order.items?.map((item, i) => (
                              <div key={i} className="flex justify-between text-sm">
                                <span className="text-zinc-300">{item.name} <span className="text-zinc-500">({item.size}) x{item.quantity}</span></span>
                                <span className="text-zinc-400">{formatPrice(item.price * item.quantity)}</span>
                              </div>
                            ))}
                            <div className="border-t border-zinc-800 pt-1 mt-2 flex justify-between text-sm">
                              <span className="text-zinc-500">Shipping</span>
                              <span className="text-zinc-400">{formatPrice(order.shipping_cost || 0)}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Shipping Address */}
                        <div className="bg-zinc-900/50 rounded-lg p-3">
                          <h4 className="text-xs font-bold text-zinc-400 mb-2 uppercase">Ship To</h4>
                          {order.shipping_address && (
                            <div className="text-sm space-y-1">
                              <p className="text-white font-medium">{order.shipping_address.name}</p>
                              <p className="text-zinc-400">{order.shipping_address.email}</p>
                              <p className="text-zinc-500">{order.shipping_address.line1}</p>
                              {order.shipping_address.line2 && <p className="text-zinc-500">{order.shipping_address.line2}</p>}
                              <p className="text-zinc-500">{order.shipping_address.city}, {order.shipping_address.postal_code}</p>
                              <p className="text-zinc-500">{order.shipping_address.country}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Status Update */}
                      <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-zinc-500">Update status:</span>
                          <Select value={order.status} onValueChange={(value) => handleUpdateOrderStatus(order.id, value)}>
                            <SelectTrigger className="w-32 bg-zinc-900 border-zinc-700 text-white text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-700">
                              <SelectItem value="pending" className="text-yellow-400">Pending</SelectItem>
                              <SelectItem value="paid" className="text-green-400">Paid</SelectItem>
                              <SelectItem value="shipped" className="text-blue-400">Shipped</SelectItem>
                              <SelectItem value="delivered" className="text-purple-400">Delivered</SelectItem>
                              <SelectItem value="cancelled" className="text-red-400">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {order.stripe_session_id && (
                          <span className="text-xs text-zinc-600 font-mono">Stripe: {order.stripe_session_id.slice(0, 20)}...</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </TabsContent>

          {/* Promotion Clients Tab */}
          <TabsContent value="promotion">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-syne font-bold text-white">Promotion Clients</h2>
                <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-accent-primary hover:bg-accent-secondary rounded-full" data-testid="add-client-btn">
                      <FaPlus className="mr-2" /> Add Client
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-background-surface border-zinc-800">
                    <DialogHeader>
                      <DialogTitle className="text-white font-syne">Add Promotion Client</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label className="text-zinc-400">Client Name</Label>
                        <Input
                          value={newClient}
                          onChange={(e) => setNewClient(e.target.value)}
                          placeholder="e.g., Artist Name"
                          className="bg-zinc-900 border-zinc-700 text-white mt-1"
                          data-testid="client-name-input"
                        />
                      </div>
                      <Button onClick={handleAddClient} className="w-full bg-accent-primary hover:bg-accent-secondary rounded-full" data-testid="save-client-btn">
                        Add Client
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <p className="text-zinc-500 text-sm mb-2">
                Manage the list of artists displayed on the Promotion page. Total: {promotionClients.length} clients
              </p>
              <p className="text-zinc-600 text-xs mb-4">
                Click the star to feature an artist in the carousel. Featured: {promotionClients.filter(c => c.featured).length}
              </p>

              {promotionClients.length === 0 ? (
                <div className="text-center py-12 bg-background-surface rounded-xl border border-zinc-800">
                  <FaBullhorn className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
                  <p className="text-zinc-500">No promotion clients yet. Add your first client!</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="clients-list">
                  {promotionClients.map((client) => (
                    <div
                      key={client.id}
                      className={`bg-background-surface border rounded-lg p-3 flex items-center justify-between group ${
                        client.featured ? 'border-yellow-500/50' : 'border-zinc-800'
                      }`}
                      data-testid={`client-item-${client.id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <button
                          onClick={() => handleToggleFeatured(client.id, client.featured)}
                          className={`flex-shrink-0 transition-colors ${
                            client.featured ? 'text-yellow-500' : 'text-zinc-600 hover:text-yellow-500'
                          }`}
                          title={client.featured ? "Remove from carousel" : "Add to carousel"}
                        >
                          <FaStar className="w-4 h-4" />
                        </button>
                        <span className="text-sm text-zinc-300 truncate">{client.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClient(client.id)}
                        className="text-zinc-500 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`delete-client-${client.id}`}
                      >
                        <FaTrash className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Artist Image Overrides */}
              <div className="border-t border-zinc-800 pt-6 mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-syne font-bold text-white flex items-center gap-2">
                    <FaImage /> Artist Image Overrides
                  </h3>
                  <Dialog open={promoImageDialogOpen} onOpenChange={setPromoImageDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-300">
                        <FaPlus className="mr-2" /> Add Image
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-background-surface border-zinc-800">
                      <DialogHeader>
                        <DialogTitle className="text-white font-syne">Upload Artist Image</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label className="text-zinc-400">Artist Name (exact match)</Label>
                          <Input
                            value={newPromoImage.name}
                            onChange={(e) => setNewPromoImage({ ...newPromoImage, name: e.target.value })}
                            placeholder="e.g., A.G. Cook"
                            className="bg-zinc-900 border-zinc-700 text-white mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-400">Image File</Label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setNewPromoImage({ ...newPromoImage, file: e.target.files[0] })}
                            className="w-full mt-1 text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-zinc-800 file:text-white hover:file:bg-zinc-700"
                          />
                        </div>
                        {newPromoImage.file && (
                          <div className="flex justify-center">
                            <img 
                              src={URL.createObjectURL(newPromoImage.file)} 
                              alt="Preview" 
                              className="w-20 h-20 object-cover rounded-full" 
                            />
                          </div>
                        )}
                        <Button 
                          onClick={handleAddPromoImage} 
                          disabled={uploadingImage}
                          className="w-full bg-accent-primary hover:bg-accent-secondary rounded-full"
                        >
                          {uploadingImage ? "Uploading..." : "Upload Image"}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <p className="text-zinc-500 text-sm mb-4">
                  Manually set images for artists when Deezer doesn't find the right one. {promoImages.length} overrides set.
                </p>
                {promoImages.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {promoImages.map((item) => (
                      <div key={item.name} className="bg-zinc-900 border border-zinc-800 rounded-lg p-2 text-center group relative">
                        <img src={item.image_url} alt={item.name} className="w-12 h-12 mx-auto rounded-full object-cover mb-2" />
                        <p className="text-xs text-zinc-400 truncate">{item.name}</p>
                        <button
                          onClick={() => handleDeletePromoImage(item.name)}
                          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </TabsContent>

          {/* News Tab */}
          <TabsContent value="news">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-syne font-bold text-white">News Articles</h2>
                <Dialog open={newsDialogOpen} onOpenChange={setNewsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-accent-primary hover:bg-accent-secondary rounded-full" data-testid="add-news-btn">
                      <FaPlus className="mr-2" /> Add Article
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-background-surface border-zinc-800">
                    <DialogHeader>
                      <DialogTitle className="text-white font-syne">Add News Article</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label className="text-zinc-400">Title</Label>
                        <Input
                          value={newArticle.title}
                          onChange={(e) => setNewArticle({ ...newArticle, title: e.target.value })}
                          placeholder="Article headline"
                          className="bg-zinc-900 border-zinc-700 text-white mt-1"
                          data-testid="news-title-input"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">URL</Label>
                        <Input
                          value={newArticle.url}
                          onChange={(e) => setNewArticle({ ...newArticle, url: e.target.value })}
                          placeholder="https://..."
                          className="bg-zinc-900 border-zinc-700 text-white mt-1"
                          data-testid="news-url-input"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Source (optional)</Label>
                        <Input
                          value={newArticle.source}
                          onChange={(e) => setNewArticle({ ...newArticle, source: e.target.value })}
                          placeholder="e.g., NME, Pitchfork"
                          className="bg-zinc-900 border-zinc-700 text-white mt-1"
                          data-testid="news-source-input"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Related Artist (optional)</Label>
                        <Input
                          value={newArticle.artist}
                          onChange={(e) => setNewArticle({ ...newArticle, artist: e.target.value })}
                          placeholder="e.g., Artist Name"
                          className="bg-zinc-900 border-zinc-700 text-white mt-1"
                          data-testid="news-artist-input"
                        />
                      </div>
                      <Button onClick={handleAddNews} className="w-full bg-accent-primary hover:bg-accent-secondary rounded-full" data-testid="save-news-btn">
                        Add Article
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <p className="text-zinc-500 text-sm">
                Curate news articles to display in the "Saved" tab on the News page.
              </p>

              {newsArticles.length === 0 ? (
                <div className="text-center py-12 bg-background-surface rounded-xl border border-zinc-800">
                  <FaNewspaper className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
                  <p className="text-zinc-500">No saved news articles yet. Add your first article!</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="news-list">
                  {newsArticles.map((article) => (
                    <div
                      key={article.id}
                      className="bg-background-surface border border-zinc-800 rounded-xl p-4 flex items-start justify-between gap-4 group"
                      data-testid={`news-item-${article.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-white truncate">{article.title}</h3>
                        <div className="flex items-center gap-2 mt-1 text-xs text-zinc-500">
                          {article.source && <span>{article.source}</span>}
                          {article.artist && (
                            <span className="px-2 py-0.5 bg-zinc-800 rounded-full">{article.artist}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <a
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-zinc-500 hover:text-white p-2"
                        >
                          <FaExternalLinkAlt className="w-3 h-3" />
                        </a>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteNews(article.id)}
                          className="text-zinc-500 hover:text-red-500"
                          data-testid={`delete-news-${article.id}`}
                        >
                          <FaTrash className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </TabsContent>

          {/* DSP Services Tab */}
          <TabsContent value="dsp">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-syne font-bold text-white">DSP Services</h2>
                <Dialog open={dspDialogOpen} onOpenChange={setDspDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-accent-primary hover:bg-accent-secondary rounded-full" data-testid="add-dsp-btn">
                      <FaPlus className="mr-2" /> Add DSP
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-background-surface border-zinc-800">
                    <DialogHeader>
                      <DialogTitle className="text-white font-syne">Add DSP Service</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label className="text-zinc-400">Service Name</Label>
                        <Input
                          value={newDSP.name}
                          onChange={(e) => setNewDSP({ ...newDSP, name: e.target.value })}
                          placeholder="e.g., Audiomack"
                          className="bg-zinc-900 border-zinc-700 text-white mt-1"
                          data-testid="dsp-name-input"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Icon Key</Label>
                        <Input
                          value={newDSP.icon}
                          onChange={(e) => setNewDSP({ ...newDSP, icon: e.target.value })}
                          placeholder="e.g., audiomack"
                          className="bg-zinc-900 border-zinc-700 text-white mt-1"
                          data-testid="dsp-icon-input"
                        />
                        <p className="text-xs text-zinc-500 mt-1">Use lowercase, no spaces (spotify, apple, youtube, etc.)</p>
                      </div>
                      <Button onClick={handleAddDSP} className="w-full bg-accent-primary hover:bg-accent-secondary rounded-full" data-testid="save-dsp-btn">
                        Add Service
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dsp-list">
                {dspServices.map((dsp) => (
                  <div
                    key={dsp.id}
                    className="bg-background-surface border border-zinc-800 rounded-xl p-4 flex items-center justify-between"
                    data-testid={`dsp-item-${dsp.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                        <FaSpotify className="w-5 h-5 text-zinc-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{dsp.name}</p>
                        <p className="text-xs text-zinc-500">{dsp.icon}</p>
                      </div>
                    </div>
                    {!dsp.is_default && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteDSP(dsp.id)}
                        className="text-zinc-400 hover:text-red-500"
                        data-testid={`delete-dsp-${dsp.id}`}
                      >
                        <FaTrash />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          </TabsContent>

          {/* Social Platforms Tab */}
          <TabsContent value="social">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-syne font-bold text-white">Social Platforms</h2>
                <Dialog open={socialDialogOpen} onOpenChange={setSocialDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-accent-primary hover:bg-accent-secondary rounded-full" data-testid="add-social-btn">
                      <FaPlus className="mr-2" /> Add Platform
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-background-surface border-zinc-800">
                    <DialogHeader>
                      <DialogTitle className="text-white font-syne">Add Social Platform</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label className="text-zinc-400">Platform Name</Label>
                        <Input
                          value={newSocial.name}
                          onChange={(e) => setNewSocial({ ...newSocial, name: e.target.value })}
                          placeholder="e.g., Discord"
                          className="bg-zinc-900 border-zinc-700 text-white mt-1"
                          data-testid="social-name-input"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Icon Key</Label>
                        <Input
                          value={newSocial.icon}
                          onChange={(e) => setNewSocial({ ...newSocial, icon: e.target.value })}
                          placeholder="e.g., discord"
                          className="bg-zinc-900 border-zinc-700 text-white mt-1"
                          data-testid="social-icon-input"
                        />
                        <p className="text-xs text-zinc-500 mt-1">Use lowercase, no spaces (instagram, twitter, facebook, etc.)</p>
                      </div>
                      <Button onClick={handleAddSocial} className="w-full bg-accent-primary hover:bg-accent-secondary rounded-full" data-testid="save-social-btn">
                        Add Platform
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="social-list">
                {socialPlatforms.map((social) => (
                  <div
                    key={social.id}
                    className="bg-background-surface border border-zinc-800 rounded-xl p-4 flex items-center justify-between"
                    data-testid={`social-item-${social.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                        <FaInstagram className="w-5 h-5 text-zinc-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{social.name}</p>
                        <p className="text-xs text-zinc-500">{social.icon}</p>
                      </div>
                    </div>
                    {!social.is_default && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSocial(social.id)}
                        className="text-zinc-400 hover:text-red-500"
                        data-testid={`delete-social-${social.id}`}
                      >
                        <FaTrash />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
